/**
 * The editor's "protect on-air parts" safety (`lib/editor/onAirGuard.ts`):
 *
 * - `OnAirProtection`, in the toolbar while this rundown has a part on air or
 *   next: locked (edits to those parts ask first) or unlocked for a while,
 *   with the time left; a click toggles.
 * - `OnAirEditDialog`, the question an edit to a protected part asks: apply
 *   it once, apply it and unlock for a while, or cancel (nothing is written).
 *
 * Presentational: the view owns the state and the pending edit.
 */
import { LockIcon, LockOpenIcon } from 'lucide-react';
import { formatDuration } from '../../lib/editor/duration.js';
import type { PlayoutMark } from '../../lib/editor/onAirGuard.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';

export type OnAirProtectionProps = {
  /** Whether any part of this rundown is on air or next. Nothing renders otherwise. */
  readonly live: boolean;
  readonly locked: boolean;
  /** While unlocked: how long until it re-locks itself. */
  readonly remainingMs: number | null;
  readonly onLock: () => void;
  readonly onUnlock: () => void;
};

export function OnAirProtection({ live, locked, remainingMs, onLock, onUnlock }: OnAirProtectionProps) {
  if (!live) return null;
  if (locked) {
    return (
      <Button size="sm" variant="outline" onClick={onUnlock} data-testid="on-air-protection" data-locked="true" title="Edits to the on-air and next parts ask first. Click to unlock for a while.">
        <LockIcon /> On-air parts protected
      </Button>
    );
  }
  return (
    <Button size="sm" variant="next" onClick={onLock} data-testid="on-air-protection" data-locked="false" title="On-air and next parts can be edited without asking. Click to protect them again.">
      <LockOpenIcon /> Unlocked{remainingMs === null ? '' : ` · ${formatDuration(Math.ceil(remainingMs / 1000) * 1000)}`}
    </Button>
  );
}

export type OnAirEditDialogProps = {
  readonly open: boolean;
  /** The protected parts the edit would change, as the tree names them. */
  readonly parts: readonly { readonly name: string; readonly mark: PlayoutMark }[];
  /** How long "apply and unlock" unlocks for, for the button's label. */
  readonly unlockMs: number;
  readonly onApply: () => void;
  readonly onApplyAndUnlock: () => void;
  readonly onCancel: () => void;
};

export function OnAirEditDialog({ open, parts, unlockMs, onApply, onApplyAndUnlock, onCancel }: OnAirEditDialogProps) {
  const onAir = parts.some((p) => p.mark === 'onair');
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent data-testid="on-air-edit-dialog">
        <DialogHeader>
          <DialogTitle>{onAir ? 'Change a part that is on air?' : 'Change the next part?'}</DialogTitle>
          <DialogDescription>This rundown is running. The edit changes {parts.length === 1 ? 'this part' : 'these parts'}:</DialogDescription>
        </DialogHeader>
        <ul className="grid gap-1 text-sm" data-testid="on-air-edit-parts">
          {parts.map((p, i) => (
            <li key={`${String(i)}-${p.name}`} className="flex items-center gap-2">
              <Badge variant={p.mark}>{p.mark === 'onair' ? 'on air' : 'next'}</Badge>
              {p.name}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" variant="outline" onClick={onApplyAndUnlock} data-testid="on-air-apply-unlock">
            Apply and unlock for {String(Math.round(unlockMs / 60_000))} min
          </Button>
          <Button type="button" variant="destructive" onClick={onApply} autoFocus data-testid="on-air-apply">
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
